//Main component
import {
  Component,
  OnInit,
  HostListener,
  NgZone,
  ChangeDetectorRef,
} from "@angular/core";
import { interval, Subscription } from "rxjs";
import { Router, NavigationEnd, RouterOutlet } from "@angular/router";
import { finalize } from "rxjs/operators";
import { Title } from "@angular/platform-browser";
import { LoginStateService } from "./services/login-state.service";
import { StringService } from "./services/string.service";
import { CertificateHolderService } from "./services/certificate-holder.service";
import { CertificateHolder } from "./classes/certificateHolder";
import { AuthenticationService } from "./services/authentication.service";
import { LoginComponent } from "./components/connection/login/login.component";
import { HeaderComponent } from "./components/shared/header/header.component";
import { StreamCertificateComponent } from "./components/shared/stream-certificate/stream-certificate.component";
import { ChangeLogComponent } from "./components/user-page/change-log/change-log.component";

@Component({
  selector: "app-root",
  standalone: true,
  imports: [
    RouterOutlet,
    LoginComponent,
    HeaderComponent,
    StreamCertificateComponent,
    ChangeLogComponent,
  ],
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit {
  public userPage: boolean = false;
  public showLogin: boolean = false;
  private loginStateSub: Subscription;
  private holderSub: Subscription;
  private routerSub: Subscription;
  private intervalSub: Subscription;
  private holderRequestSub: Subscription;

  constructor(
    private auth: AuthenticationService,
    private router: Router,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private titleService: Title,
    public loginStateService: LoginStateService,
    public strings: StringService,
    public holderService: CertificateHolderService,
  ) {}

  //On focus check if client is valid
  @HostListener("window:focus")
  onFocus(): void {
    if (
      this.router.url.indexOf("/login/access-denied") === -1 &&
      this.router.url.indexOf("/validate") === -1
    ) {
      this.auth.validateSession();
    } else {
      this.loginStateService.toggle(false);
    }
  }

  ngOnInit() {
    //CHeck if client is valid
    this.auth.validateSession();
    //Get strings
    this.strings.getAppStrings();
    this.strings.getConnectionStrings();

    //On change to holder, set title of page to holder name and get strings if certificate language different from application language
    this.holderSub = this.holderService.onChange.subscribe(
      (holder: CertificateHolder) => {
        this.titleService.setTitle(holder.full_name);
        if (holder.active_certificate.language !== undefined) {
          if (
            holder.active_certificate.language.iso2_code !==
            localStorage.getItem("language")
          ) {
            this.strings.getCertificateStrings(
              holder.active_certificate.language.iso1_code,
            );
          }
        }
      },
    );

    //Initial route load
    this.loadHolderFromUrl(this.router.url);

    //On route change, if user page, get certificate holder id and retrieve data
    this.routerSub = this.router.events.subscribe((q: NavigationEnd) => {
      if (q instanceof NavigationEnd) {
        this.loadHolderFromUrl(q.urlAfterRedirects || q.url);
      }
    });
    //Check if client is valid every 5 minutes
    const i = interval(1000 * 60 * 5);
    this.intervalSub = i.subscribe(() => {
      if (this.loginStateService.loggedIn) {
        this.auth.validateSession();
      }
    });
  }

  //Unsubscribe on route change
  ngOnDestroy() {
    if (this.loginStateSub) this.loginStateSub.unsubscribe();
    if (this.routerSub) this.routerSub.unsubscribe();
    if (this.holderSub) this.holderSub.unsubscribe();
    if (this.intervalSub) this.intervalSub.unsubscribe();
    if (this.holderRequestSub) this.holderRequestSub.unsubscribe();
    this.holderService.setLoading(false);
  }

  //Determine if current page is main page or user page
  private isUserPage(route: any): boolean {
    if (
      route === undefined ||
      route instanceof NavigationEnd === false ||
      route.url === undefined
    )
      return false;
    let path = route.url.split("/");
    if (path.length > 2 && path[1] === "user") return true;
    return false;
  }

  private loadHolderFromUrl(url: string): void {
    const path = (url || "").split("/");
    this.userPage = path.length > 2 && path[1] === "user";
    if (!this.userPage) {
      this.holderService.setLoading(false);
      return;
    }

    const holderId = Number(path[2]);
    if (Number.isNaN(holderId)) {
      this.holderService.setLoading(false);
      return;
    }

    if (this.holderRequestSub) {
      this.holderRequestSub.unsubscribe();
    }

    this.holderService.setLoading(true);

    this.holderRequestSub = this.holderService
      .getCertificateHolder(holderId)
      .pipe(
        finalize(() => {
          this.ngZone.run(() => {
            this.holderService.setLoading(false);
            this.cdr.detectChanges();
          });
        }),
      )
      .subscribe(
        (holder) => {
          this.ngZone.run(() => {
            try {
              this.holderService.toggleChange(new CertificateHolder(holder));
            } catch {
              //Keep app responsive even if backend payload shape is unexpected
            }
            this.cdr.detectChanges();
          });
        },
        () => {
          //Error path handled by finalize
        },
      );
  }
}
